/**
 * Deadpan remarks for copy-related toast messages.
 * Each pool has 12+ unique messages that are randomly selected.
 */

const urlCopyRemarks: string[] = [
  'Link copied. Do with it what you will.',
  'The URL now lives in your clipboard.',
  'Copied. Someone\'s about to get a link.',
  'Link acquired. Use responsibly.',
  'Your clipboard has been updated.',
  'Link copied. The internet awaits.',
  'Ctrl+V whenever you\'re ready.',
  'It\'s in your clipboard now. Go forth.',
  'Link secured. Mission accomplished.',
  'Copied. Share at your own discretion.',
  'The link has been extracted.',
  'Clipboard status: occupied.',
  'Link obtained. Social obligations pending.',
  'Copy complete. Now what?',
  'Link copied. Someone will be thrilled.',
];

const colorCopyRemarks: string[] = [
  'Colors copied. Your clipboard is now chromatic.',
  'The palette has been acquired.',
  'Colors secured. Paint something nice.',
  'Copied. Your clipboard is feeling colorful.',
  'Color codes extracted successfully.',
  'Hex values obtained. Very technical.',
  'Colors copied. Use them wisely.',
  'Your clipboard now contains art supplies.',
  'Palette acquired. Designer mode activated.',
  'Colors in your clipboard. You\'re welcome.',
  'Copied. Someone\'s starting a project.',
  'The swatches have been liberated.',
  'Colors copied. Inspiration is portable now.',
  'Hex codes secured. Proceed to create.',
  'Clipboard updated with chromatic data.',
];

const bookmarkAddRemarks: string[] = [
  'Cherished. It knows.',
  'Added to the collection. Very sentimental.',
  'Saved for later contemplation.',
  'This one made the cut.',
  'Bookmarked. You can revisit your feelings now.',
  'Cherished hues updated. You\'re attached.',
  'Saved. The colors will wait for you.',
  'Added to favorites. Commitment noted.',
  'This moment is now officially special.',
  'Preserved for future nostalgia.',
  'Bookmarked. You really liked this one.',
  'Saved to your personal color vault.',
  'Cherished. The palette is honored.',
  'Added. You\'ll definitely look at this again.',
  'This one sparked something, apparently.',
];

const bookmarkRemoveRemarks: string[] = [
  'Removed. Moving on.',
  'Uncherished. It happens.',
  'Removed from the collection. Cold.',
  'Gone from favorites. The colors understand.',
  'Unbookmarked. Feelings change.',
  'Removed. It wasn\'t meant to be.',
  'Deleted from cherished. Harsh but fair.',
  'The moment has been released.',
  'Removed. You\'ve evolved.',
  'Unbookmarked. No hard feelings.',
  'Gone. The palette will recover.',
  'Removed from the vault. Space cleared.',
  'Uncherished. Time to move forward.',
  'Deleted. Attachment severed.',
  'Removed. It served its purpose.',
];

const scrollTooLongRemarks: string[] = [
  'You\'ve been scrolling for a while. The colors will still be here.',
  'One minute of scrolling. Maybe pick one and sit with it?',
  'That\'s a lot of scrolling. Everything okay over there?',
  'You could have looked at a real sunset by now.',
  'Scrolling won\'t fill the void, but these colors might help.',
  'Have you considered... stopping?',
  'A minute of scrolling. The colors appreciate your dedication.',
  'Maybe go outside and see some colors in person.',
  'Your thumb must be tired. Take a moment.',
  'Slow down. These moments aren\'t going anywhere.',
];

const deleteConfirmRemarks: string[] = [
  'Are you sure? Tap again to confirm.',
  'Really? Tap again if you mean it.',
  'This is permanent. Tap again to proceed.',
  'The colors will be gone. Tap again.',
  'No take-backs after this. Tap again.',
  'Final answer? Tap again to delete.',
  'Think about it. Then tap again.',
  'Once more to seal its fate.',
  'You sure about this? Tap again.',
  'The moment awaits judgment. Tap again.',
  'Goodbye forever? Tap again.',
  'Last chance to reconsider. Or tap again.',
  'This can\'t be undone. Tap again.',
  'One more tap and it\'s history.',
  'Feeling decisive? Tap again.',
];

const deleteSuccessRemarks: string[] = [
  'Gone. The colors have moved on.',
  'Deleted. It never happened.',
  'The moment has been erased.',
  'Poof. Just like that.',
  'Deleted. The palette is at peace.',
  'Gone forever. Or is it?',
  'Removed from existence. Dramatic.',
  'The colors have left the building.',
  'Deleted. Time to make new memories.',
  'It\'s gone. You\'re free now.',
  'Erased. The void welcomes it.',
  'Deleted. Moving on is healthy.',
  'The moment has been retired.',
  'Gone. Your feed feels lighter.',
  'Deleted. No one will ever know.',
];

/**
 * Get a random deadpan remark for copying a URL.
 */
export function getUrlCopyToast(): string {
  return urlCopyRemarks[Math.floor(Math.random() * urlCopyRemarks.length)];
}

/**
 * Get a random deadpan remark for copying colors.
 */
export function getColorCopyToast(): string {
  return colorCopyRemarks[Math.floor(Math.random() * colorCopyRemarks.length)];
}

/**
 * Get a random deadpan remark for scrolling too long.
 */
export function getScrollTooLongToast(): string {
  return scrollTooLongRemarks[Math.floor(Math.random() * scrollTooLongRemarks.length)];
}

/**
 * Get a random deadpan remark for adding a bookmark.
 */
export function getBookmarkAddToast(): string {
  return bookmarkAddRemarks[Math.floor(Math.random() * bookmarkAddRemarks.length)];
}

/**
 * Get a random deadpan remark for removing a bookmark.
 */
export function getBookmarkRemoveToast(): string {
  return bookmarkRemoveRemarks[Math.floor(Math.random() * bookmarkRemoveRemarks.length)];
}

/**
 * Get a random deadpan remark for delete confirmation.
 */
export function getDeleteConfirmToast(): string {
  return deleteConfirmRemarks[Math.floor(Math.random() * deleteConfirmRemarks.length)];
}

/**
 * Get a random deadpan remark for successful deletion.
 */
export function getDeleteSuccessToast(): string {
  return deleteSuccessRemarks[Math.floor(Math.random() * deleteSuccessRemarks.length)];
}
